import React, { Component, useState } from 'react';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { Button } from '@mui/material';

import { getStripeCredentials } from '~/services/PaymentsService';

import getStripeAmount from '~/utils/getStripeAmount';

import ErrorDisplay from '../ErrorDisplay';

const stripePromiseCache = new Map();

function getStripePromise(publishableKey, stripeAccount) {
  const cacheKey = JSON.stringify([publishableKey, stripeAccount ?? null]);

  if (!stripePromiseCache.has(cacheKey)) {
    stripePromiseCache.set(cacheKey, loadStripe(publishableKey, stripeAccount ? { stripeAccount } : undefined));
  }

  return stripePromiseCache.get(cacheKey);
}

function StripeWidgets({
  disabled,
  onPayment,
  paymentWillStart,
  customerName,
  customerEmail,
  regionCode,
  packagePrice,
  currencyCode,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const billingDetails = {
    name: customerName,
    email: customerEmail,
    ...(regionCode && {
      address: {
        country: regionCode,
      },
    }),
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError({ message: 'Payment form is not ready. Please try again.' });
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      setError(submitError);
      return;
    }

    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          name: customerName,
        },
      },
    });

    if (paymentMethodError) {
      setError(paymentMethodError);
      return;
    }

    paymentWillStart(() => {
      onPayment('stripe', {
        amount: packagePrice,
        paymentMethodId: paymentMethod.id,
        confirmPayment: async (clientSecret) => {
          const { error: confirmError } = await stripe.confirmPayment({
            clientSecret,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: 'if_required',
          });

          if (confirmError) {
            throw confirmError;
          }
        },
      });
    });
  };

  return (
    <div>
      {error?.message && <ErrorDisplay message={error.message} />}

      <PaymentElement
        options={{
          layout: 'tabs',
          defaultValues: {
            billingDetails,
          },
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never',
          },
        }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handlePayment}
        className="submit-payment-btn"
        disabled={disabled || !stripe || !elements}
        sx={{ mt: 1 }}
        fullWidth
      >
        Submit Payment
      </Button>
    </div>
  );
}

class StripePayment extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dataLoaded: false,
      stripePublishableKey: '',
      stripeAccount: null,
    };
  }

  componentDidMount() {
    getStripeCredentials(this.props.currencyCode, this.props.tenant.brand)
      .then((response) => {
        this.setState({
          stripePublishableKey: response.result.stripe_publishable_key,
          stripeAccount: response.result.stripe_account,
          dataLoaded: true,
        });
      })
      .catch((error) => {
        this.setState({ error });
      });
  }

  render() {
    const { dataLoaded, error, stripePublishableKey, stripeAccount } = this.state;
    const { currencyCode, packagePrice } = this.props;

    if (!dataLoaded) {
      return <div>Loading...</div>;
    }

    if (error) {
      return <div>Error loading the Stripe credentials: {error.message || String(error)}</div>;
    }

    const stripePromise = getStripePromise(stripePublishableKey, stripeAccount);
    const elementsOptions = {
      mode: 'payment',
      amount: getStripeAmount(packagePrice, currencyCode),
      currency: currencyCode.toLowerCase(),
      paymentMethodCreation: 'manual',
      paymentMethodTypes: ['card'],
    };

    return (
      <Elements stripe={stripePromise} options={elementsOptions}>
        <StripeWidgets {...this.props} />
      </Elements>
    );
  }
}

export default StripePayment;
